// ABOUTME: Seeds local state from a value that only exists after the first render
// ABOUTME: A useState initializer alone keeps whatever the loading state was
import { useEffect, useRef } from "react"

/**
 * Calls `seed` once, as soon as `value` stops being empty — and never again, so
 * a later refetch cannot overwrite what the user has edited since.
 *
 * For local state that the user then EDITS and that is built from a fetched
 * value. A `useState` initializer alone cannot do it: on the first render the
 * value is not there yet, the initializer takes whatever fallback the code
 * offers, and nothing ever replaces it.
 *
 * Not for values the user does not edit — derive those from the fetched value
 * instead of copying them into state. A copy has to be kept in sync; a derived
 * value cannot go stale. (NutritionStatistics learned this the hard way: the
 * carb-loading target was copied, went stale, and since it is also what the
 * save sends, it wrote the wrong target back to the server.)
 *
 * Empty means falsy, so `0` and `""` count as "not arrived" — pass something
 * nullable, or wrap it.
 *
 * `seed` IS a dependency even though the caller rebuilds it every render: the latch below makes
 * such a re-run a no-op, so listing it costs nothing and keeps the list honest. The suppression it
 * replaces was worse than the dependency — an inline eslint-disable of a react-hooks rule makes the
 * React Compiler skip the component that calls this hook.
 *
 * The latch is a ref, written BEFORE the call: StrictMode runs the effect body,
 * its cleanup and the body again within one commit, and a `useState` guard is
 * read from the same render snapshot both times — so "once" would have meant
 * twice for any consumer whose seed is not idempotent.
 */
export function useSeedOnArrival<T>(value: T | null | undefined, seed: (value: T) => void): void {
  // Already there at mount means the initializers used it: nothing to seed.
  const hasSeeded = useRef<boolean>(Boolean(value))

  useEffect(() => {
    if (hasSeeded.current || !value) return
    hasSeeded.current = true
    seed(value)
  }, [value, seed])
}
